import type { AsyncDuckDB, AsyncDuckDBConnection } from '@duckdb/duckdb-wasm';

import {
  DEFAULT_EXTRACT_POPULATION,
  FULL_SCOPE,
  type BuildingProgress,
  type ExtractPipeline,
  type ExtractPopulation,
  type ExtractScope,
  fetchExtract,
  populationFingerprint,
  scopeFingerprint,
} from '../services/reportingApi';
import type { ColumnType } from '../types/reporting';
import { SharedCancellation } from '../utils/sharedCancellation';
import {
  arrowFieldTypeToColumnType,
  arrowToRows,
  type ResultRow,
} from './arrowToRows';

export class DuckDBUnsupportedError extends Error {}

// What the dataset's DuckDB view currently reflects. The view NAME is the bare
// dataset key (the compiler emits `FROM "visits"`), so a dataset can hold only
// ONE scope's rows at a time — which is precisely why registration and querying
// have to happen under one lock (see withDatasets).
interface Registration {
  representationFp: string;
  etag: string;
  // Identity of the ArrayBuffer last registered. A forced refresh bypasses the
  // ETag skip — an ETag can be absent (a 202-then-200 path, or any deployment
  // where the header is not readable), and '' === '' is an unreliable "unchanged"
  // signal — so it needs a separate idempotency key to dedup two
  // *coalesced* forces — which share reportingApi's single ExtractResult, hence
  // the identical buffer reference — without suppressing a genuinely new
  // rebuild, whose buffer is a different reference.
  buffer: ArrayBuffer;
  // Server build stamp of these bytes. Retained so a memory-served run (see
  // withDatasets) can still answer "Data as of" without a network round trip.
  builtAt: string | null;
  // Monotonic stamp of THIS registration. withDatasets samples a registered
  // extract in phase 1, outside the session lock; comparing the sampled stamp
  // against the live one inside the lock is how it detects that someone else
  // (a forced refresh) registered newer bytes while it queued.
  generation: number;
  // Date.now() when the server last confirmed these bytes (a 200 or a 304).
  checkedAt: number;
}

// A memory-served run older than this sends a conditional GET first, so an open
// tab picks up the nightly build. A 304 keeps the registration.
const RECHECK_AFTER_MS = 30 * 60 * 1000;

// Monotonically increasing across every registration, for every dataset. Never
// reset: a sample only ever compares against a later read of the same map entry,
// so all that matters is that a newer registration never reuses an older stamp.
let registrationGeneration = 0;

interface EngineHandle {
  db: AsyncDuckDB;
  conn: AsyncDuckDBConnection;
  // Retained so the bench-only isolated-engine lifecycle can fully terminate an
  // engine it booted (disposeIsolatedEngine). Production never terminates the
  // shared singleton.
  worker: Worker;
  registered: Map<string, Registration>;
}

let enginePromise: Promise<EngineHandle> | null = null;

const q = (ident: string): string => `"${ident.replace(/"/g, '""')}"`;

const logTiming = (event: string, start: number, dataset?: string): void => {
  console.debug('[reporting]', {
    event,
    ...(dataset !== undefined ? { dataset } : {}),
    ms: Math.round(performance.now() - start),
  });
};

async function initEngine(): Promise<EngineHandle> {
  const start = performance.now();
  // Tracked outside the try so a failure AFTER the worker spawned can terminate
  // it. getEngine() clears enginePromise on failure so the user can retry, and
  // without this every retry would strand another ~35MB DuckDB worker — a
  // browser that fails init once (no WASM, blocked worker URL) is exactly the
  // browser that retries, so the leak compounds precisely where it hurts.
  let worker: Worker | undefined;
  try {
    // Dynamic imports keep the ~35MB engine out of the main bundle; the
    // Reports pages are the only consumers.
    const duckdb = await import('@duckdb/duckdb-wasm');
    const [mvpWasm, mvpWorker, ehWasm, ehWorker] = await Promise.all([
      import('@duckdb/duckdb-wasm/dist/duckdb-mvp.wasm?url'),
      import('@duckdb/duckdb-wasm/dist/duckdb-browser-mvp.worker.js?url'),
      import('@duckdb/duckdb-wasm/dist/duckdb-eh.wasm?url'),
      import('@duckdb/duckdb-wasm/dist/duckdb-browser-eh.worker.js?url'),
    ]);
    const bundle = await duckdb.selectBundle({
      mvp: { mainModule: mvpWasm.default, mainWorker: mvpWorker.default },
      eh: { mainModule: ehWasm.default, mainWorker: ehWorker.default },
    });
    worker = new Worker(bundle.mainWorker!);
    const db = new duckdb.AsyncDuckDB(new duckdb.VoidLogger(), worker);
    await db.instantiate(bundle.mainModule, bundle.pthreadWorker);
    const conn = await db.connect();
    logTiming('engine_init', start);
    return { db, conn, worker, registered: new Map() };
  } catch (error) {
    // Best-effort: a worker that never finished instantiating may already be
    // dead, and terminate() throwing here would mask the real init failure.
    try {
      worker?.terminate();
    } catch {
      /* already gone */
    }
    console.error('DuckDB-WASM failed to initialize', error);
    throw new DuckDBUnsupportedError('DuckDB-WASM failed to initialize');
  }
}

async function getEngine(): Promise<EngineHandle> {
  if (!enginePromise) enginePromise = initEngine();
  try {
    return await enginePromise;
  } catch (error) {
    enginePromise = null; // allow retry on next call
    throw error;
  }
}

// Bumped each time a dataset's Parquet is (re-)registered into DuckDB (a new
// ETag, or a forced refresh). Consumers that cache data derived from the extract
// — e.g. useColumnValues' distinct filter-value lists, which otherwise live for
// the whole session — subscribe so a refresh that mutated the underlying rows
// invalidates them instead of leaving pre-refresh options in the combobox.
const datasetVersions = new Map<string, number>();
const datasetVersionListeners = new Set<() => void>();

export function getDatasetVersion(datasetKey: string): number {
  return datasetVersions.get(datasetKey) ?? 0;
}

export function subscribeDatasetVersions(listener: () => void): () => void {
  datasetVersionListeners.add(listener);
  return () => datasetVersionListeners.delete(listener);
}

function bumpDatasetVersion(datasetKey: string): void {
  datasetVersions.set(datasetKey, (datasetVersions.get(datasetKey) ?? 0) + 1);
  datasetVersionListeners.forEach(listener => listener());
}

// The ONE low-level registration path: production (registerIfNeeded, driven by
// withDatasets) and the dev benchmark (joinBench) both go through here —
// dropFile → registerFileBuffer over a COPY of the JS ArrayBuffer → CREATE VIEW
// over read_parquet — so a benchmark registering through this helper measures
// the same mechanics users actually hit (codex round-2: the harness must not
// approximate the production path). The buffer.slice(0) copy is load-bearing:
// registerFileBuffer transfers/detaches the bytes it is handed, so the copy
// keeps the caller-retained ArrayBuffer (reportingApi's extractCache in
// production, the bench's extractCache mirror) alive and re-registrable.
export async function registerExtractBuffer(
  engine: { db: AsyncDuckDB; conn: AsyncDuckDBConnection },
  datasetKey: string,
  buffer: ArrayBuffer,
  // Distinguishes one representation of a dataset from another. The VIEW name
  // must stay the bare dataset key (the compiler emits FROM "visits"), but the
  // registered FILE must not: see the scope-transition note below.
  fileTag = 'full'
): Promise<void> {
  // The file name carries the scope. Reusing one file name across scopes means
  // dropFile + registerFileBuffer swaps the bytes underneath a view that already
  // points at that name, and DuckDB then reads the new buffer through parquet
  // metadata it resolved from the old one — surfacing as
  // "Invalid Error: invalid TType" (a Thrift decode failure) the moment a
  // windowed report is joined against a second dataset. Deterministic on the
  // flag-ON path; invisible with the flag off, because the dark path never sends
  // a scope so every dataset only ever has one representation.
  // ...and the same hazard exists WITHOUT a scope change: a manual refresh
  // re-registers a rebuilt snapshot at the SAME scope, so a scope-only tag would
  // reuse the file name and swap bytes under the live view exactly as above.
  // Every registration therefore gets a fresh name; the drop below reclaims the
  // old one once nothing points at it.
  // The representation fingerprint is an internal map key and contains NUL and
  // pipe separators. It is also used as a diagnostic file tag, so sanitize it
  // before embedding it in the SQL string literal below. A NUL in the literal
  // makes DuckDB report an unterminated quoted string even though the Parquet
  // bytes and manifest are valid.
  const safeFileTag = fileTag.replace(/[^A-Za-z0-9_.-]/g, '_');
  const fileName = `${datasetKey}__${safeFileTag}__${++fileRegistrationSeq}.parquet`;
  const previous = registeredFileNames.get(datasetKey);
  await engine.db.registerFileBuffer(fileName, new Uint8Array(buffer.slice(0)));
  await engine.conn.query(
    `CREATE OR REPLACE VIEW ${q(datasetKey)} AS SELECT * FROM read_parquet('${fileName}')`
  );
  registeredFileNames.set(datasetKey, fileName);
  // Drop the superseded file only AFTER the view points at the new one, so a
  // failed registration leaves the previous representation intact and queryable
  // rather than dropping the dataset out from under the session.
  if (previous && previous !== fileName) {
    await engine.db.dropFile(previous).catch(() => undefined);
  }
}

// dataset key -> the file name its view currently reads. Module-level (not on
// the engine handle) to mirror registeredScopes, and reset alongside it.
const registeredFileNames = new Map<string, string>();

// Monotonic suffix making every registered file name unique, so bytes are never
// swapped underneath a name DuckDB has already resolved parquet metadata for.
let fileRegistrationSeq = 0;

// One extract to make available inside a session. `scope` defaults to
// FULL_SCOPE; see the D4 note on withDatasets for who may set it.
export interface DatasetRequest {
  key: string;
  scope?: ExtractScope;
  // Shared report population for this primary or related extract request.
  population?: ExtractPopulation;
  pipeline?: ExtractPipeline;
  force?: boolean;
  // Detaches the caller from this extract's fetch (see
  // FetchExtractOptions.signal). Plain fetches coalesce HERE, so the signal is
  // aggregated into the shared entry's SharedCancellation rather than forwarded
  // straight through — one consumer unmounting must not cancel a download
  // another consumer is still awaiting.
  signal?: AbortSignal;
  // Receives 202 build updates for the primary extract. Shared plain requests
  // retain this subscriber so a later report runner can observe a fetch started
  // by another consumer.
  onBuilding?: (progress: BuildingProgress) => void;
}

// Everything a caller may do with the registered views. Deliberately the ONLY
// way to reach the connection: exporting a bare `runReportQuery` would let a
// caller query a dataset outside the lock, and the view it reads might by then
// hold a different SCOPE's rows than the one it ensured.
export interface DatasetSession {
  // Server build stamp of each requested extract (min over its selected
  // partitions, per D3's X-Extract-Built-At).
  builtAt(datasetKey: string): string | null;
  columns(datasetKey: string): Promise<Map<string, ColumnType>>;
  query(sql: string): Promise<{ columns: string[]; rows: ResultRow[] }>;
}

// In-flight PLAIN fetches, keyed by dataset, scope, and report population,
// shared by all callers (the runner's parallel extract loads, the distinct-values
// hook). Two concurrent requests for the same representation must not
// double-download it. Keying by dataset alone would collapse distinct windows or
// population identities into one response.
//
// Forced refreshes deliberately BYPASS this map: a force joining (or becoming) a
// shared plain fetch would let a plain load satisfy a force (no rebuild) or a
// force satisfy a plain caller. Forces coalesce one layer down instead, on
// reportingApi's single in-flight ?refresh=1 per (dataset, scope).
interface InflightFetch {
  promise: Promise<{
    buffer: ArrayBuffer;
    etag: string;
    builtAt: string | null;
  }>;
  cancellation: SharedCancellation;
  buildingSubscribers: Set<(progress: BuildingProgress) => void>;
}
const inflightFetches = new Map<string, InflightFetch>();

const representationFingerprint = (req: DatasetRequest): string =>
  `${scopeFingerprint(req.scope ?? FULL_SCOPE)}\u0000${populationFingerprint(req.population ?? DEFAULT_EXTRACT_POPULATION)}${req.pipeline ? `\u0000${req.pipeline}` : ''}`;

const fetchKey = (req: DatasetRequest): string =>
  `${req.key}\u0000${representationFingerprint(req)}`;

function fetchForRequest(
  req: DatasetRequest
): Promise<{ buffer: ArrayBuffer; etag: string; builtAt: string | null }> {
  const opts = {
    force: req.force,
    scope: req.scope ?? FULL_SCOPE,
    population: req.population ?? DEFAULT_EXTRACT_POPULATION,
    pipeline: req.pipeline,
  };
  // Forces bypass this map and coalesce one layer down, so their signal goes
  // straight to reportingApi, which aggregates it there.
  if (req.force) return fetchExtract(req.key, { ...opts, signal: req.signal });
  const key = fetchKey(req);
  const existing = inflightFetches.get(key);
  // A spent entry (every joined consumer aborted) must not satisfy a new caller
  // — its fetch is already unwinding. Fall through and start a fresh one.
  if (existing && !existing.cancellation.aborted) {
    existing.cancellation.join(req.signal);
    if (req.onBuilding) existing.buildingSubscribers.add(req.onBuilding);
    return existing.promise;
  }
  const cancellation = new SharedCancellation();
  cancellation.join(req.signal);
  const buildingSubscribers = new Set<(progress: BuildingProgress) => void>();
  if (req.onBuilding) buildingSubscribers.add(req.onBuilding);
  const started = performance.now();
  const promise = fetchExtract(req.key, {
    ...opts,
    signal: cancellation.signal,
    onBuilding: progress => {
      buildingSubscribers.forEach(subscriber => {
        try {
          subscriber(progress);
        } catch (error) {
          // A progress observer is best-effort. One consumer must not make the
          // shared fetch reject for every other consumer.
          console.error('Reporting build progress subscriber failed', error);
        }
      });
    },
  })
    .then(result => {
      logTiming('extract_fetch', started, req.key);
      return result;
    })
    // Clear on settle so later calls revalidate via ETag, and so a failure
    // doesn't pin a rejected promise that every later caller inherits. Guarded
    // on identity: a cancelled fetch is REPLACED in the map (spent entries are
    // skipped above) rather than settling first, so an unguarded delete would
    // let the abandoned fetch's late settle evict the live replacement — after
    // which a third caller would start a duplicate download.
    .finally(() => {
      if (inflightFetches.get(key) === entry) inflightFetches.delete(key);
    });
  const entry: InflightFetch = {
    promise,
    cancellation,
    buildingSubscribers,
  };
  inflightFetches.set(key, entry);
  return promise;
}

// Module-level mirror of each dataset's registered scope. It lives outside the
// engine handle because `currentScopeFor` is called during render and so must be
// synchronous — it cannot await the engine promise.
const registeredScopes = new Map<string, ExtractScope>();

// Point the dataset's view at these bytes if it isn't already. Runs ONLY under
// the session lock (see withDatasets), which is what replaced the old
// per-dataset FIFO register chain: the lock serializes registration AND the
// queries that follow it, so a concurrent registration can no longer swap the
// view out from under an in-flight query.
async function registerIfNeeded(
  engine: EngineHandle,
  req: DatasetRequest,
  extract: { buffer: ArrayBuffer; etag: string; builtAt: string | null }
): Promise<void> {
  const representationFp = representationFingerprint(req);
  const current = engine.registered.get(req.key);
  if (current && current.representationFp === representationFp) {
    // A forced refresh must never short-circuit on ETag equality: an absent ETag
    // reads as '' on both sides, and '' === '' would let an older registration
    // suppress the force and keep the stale view. Dedup instead on
    // buffer IDENTITY so two coalesced forces (which share reportingApi's single
    // ExtractResult, hence the same buffer reference) register once, while a
    // genuinely new rebuild — a different reference — always re-registers.
    if (req.force) {
      if (current.buffer === extract.buffer) return;
    } else if (current.etag === extract.etag) {
      // Plain path: a prior caller with the same freshly-fetched bytes already
      // registered them while we waited for the lock.
      return;
    }
  }
  // A scope or population change always falls through to registration, whatever
  // the ETags say. They are both response identity: reusing a narrow or differently
  // populated registration can answer the current report from the wrong bytes.
  const started = performance.now();
  // registerExtractBuffer registers a COPY (buffer.slice(0)) so it detaches the
  // copy, never the ArrayBuffer retained in reportingApi's extractCache. A later
  // conditional 304 hands that cached buffer back; if we detached it here and
  // CREATE VIEW then threw before the registration was recorded, every retry
  // would re-register a detached, zero-length buffer and fail forever.
  await registerExtractBuffer(
    engine,
    req.key,
    extract.buffer,
    representationFp
  );
  engine.registered.set(req.key, {
    representationFp,
    etag: extract.etag,
    buffer: extract.buffer,
    builtAt: extract.builtAt,
    generation: ++registrationGeneration,
    checkedAt: Date.now(),
  });
  registeredScopes.set(req.key, req.scope ?? FULL_SCOPE);
  logTiming('dataset_register', started, req.key);
  bumpDatasetVersion(req.key);
}

// The bytes already registered for this request's exact representation, or
// undefined if this dataset isn't loaded at this scope. Returning the retained
// buffer (rather than a bare "yes") keeps PHASE 2 uniform: the cached entry
// flows through `registerIfNeeded` like a fetched one, so if a concurrent
// consumer swapped the view to another scope between this read and the lock, it
// re-registers from memory instead of silently querying the wrong window.
//
// The `generation` rides along so PHASE 2 can tell "the bytes I sampled are
// still the live registration" from "someone registered newer bytes while I
// queued for the lock".
function registeredExtractFor(
  engine: EngineHandle,
  req: DatasetRequest
):
  | {
      buffer: ArrayBuffer;
      etag: string;
      builtAt: string | null;
      generation: number;
      checkedAt: number;
    }
  | undefined {
  const current = engine.registered.get(req.key);
  if (!current) return undefined;
  if (current.representationFp !== representationFingerprint(req)) {
    return undefined;
  }
  return {
    buffer: current.buffer,
    etag: current.etag,
    builtAt: current.builtAt,
    generation: current.generation,
    checkedAt: current.checkedAt,
  };
}

// The session lock: a FIFO promise chain covering register-through-query for
// EVERY consumer (report runner, distinct-value combobox). A dataset's view name
// is its bare key, so it holds one scope at a time; without this lock a second
// consumer could re-register a different scope between another's ensure and its
// query, and the query would silently answer from the wrong window.
let sessionChain: Promise<unknown> = Promise.resolve();

function withSessionLock<T>(fn: () => Promise<T>): Promise<T> {
  // Chain onto the previous holder, ignoring its outcome so one caller's failure
  // never rejects the next.
  const run = sessionChain.catch(() => undefined).then(fn);
  sessionChain = run.catch(() => undefined);
  return run;
}

/**
 * THE ensure-and-query boundary. Fetches every requested extract, registers the
 * ones whose bytes or SCOPE differ from what is currently registered, and runs
 * `run` with those views pinned for its whole duration.
 *
 * D4 (join completeness): only the PRIMARY dataset may carry a windowed scope.
 * Related datasets must be fetched whole, because each dataset is its own
 * extract and the join happens in the browser — a visit last month whose episode
 * started 14 months ago would otherwise fetch the visit but not its episode row,
 * and the LEFT JOIN would yield NULL episode columns instead of failing loudly.
 * The runner builds its requests through `buildDatasetRequests`, which encodes
 * that rule; this layer intentionally does not re-derive it (it cannot know
 * which dataset is primary).
 */
export async function withDatasets<T>(
  requests: readonly DatasetRequest[],
  run: (session: DatasetSession) => Promise<T>
): Promise<T> {
  const engine = await getEngine();
  // PHASE 1 — fetch OUTSIDE the lock. Network (including a 202 build poll that
  // can run for minutes) must not block another consumer's queries, and
  // concurrent forced callers must reach reportingApi's coalescing point rather
  // than queueing behind each other's rebuilds.
  //
  // A dataset already registered at this exact scope and population is not
  // fetched at all. The engine is in-memory DuckDB-WASM: once the bytes are
  // registered, re-querying
  // them — a re-sort, a re-group, a filter change, paging, an export — is pure
  // local SQL, and this is an exploration surface, not a live feed. Every such
  // run used to pay a full conditional GET whose only product was confirming
  // what was already registered.
  //
  // The skip is keyed on the REPRESENTATION, so the two cases that genuinely
  // need bytes we don't have still fetch: a changed scope (a widened window is
  // rows that are simply not in memory) and a dataset newly joined into the
  // report. `force` — the Refresh button — always fetches a rebuild. A
  // registration not checked for RECHECK_AFTER_MS revalidates by conditional GET.
  const fetched: {
    req: DatasetRequest;
    extract: { buffer: ArrayBuffer; etag: string; builtAt: string | null };
    // Set for a memory-served entry or a recheck: the registration stamp
    // sampled in phase 1, revalidated under the lock in phase 2.
    sampledGeneration?: number;
    fromNetwork?: boolean;
  }[] = await Promise.all(
    requests.map(async req => {
      const cached = req.force ? undefined : registeredExtractFor(engine, req);
      if (cached && Date.now() - cached.checkedAt < RECHECK_AFTER_MS) {
        return { req, extract: cached, sampledGeneration: cached.generation };
      }
      try {
        return {
          req,
          extract: await fetchForRequest(req),
          sampledGeneration: cached?.generation,
          fromNetwork: true,
        };
      } catch (error) {
        // A failed recheck keeps the loaded bytes, and the next run retries.
        if (!cached || req.signal?.aborted) throw error;
        console.warn(
          '[reporting]',
          { event: 'recheck_failed', dataset: req.key },
          error
        );
        return { req, extract: cached, sampledGeneration: cached.generation };
      }
    })
  );
  // PHASE 2 — register + query under the lock.
  return withSessionLock(async () => {
    const builtAt = new Map<string, string | null>();
    for (const entry of fetched) {
      const { req, sampledGeneration, fromNetwork } = entry;
      let { extract } = entry;
      // Revalidate a MEMORY-SERVED snapshot or a recheck, sampled in phase 1 outside
      // this lock, so a forced refresh may have registered newer bytes for this
      // dataset while we queued behind it. Re-registering the snapshot would put
      // the pre-refresh view back the instant after the user pressed Refresh, so
      // defer to whatever is registered NOW and let registerIfNeeded skip.
      // A no-longer-matching registration (another scope took the view) still
      // falls back to the snapshot — that is the case registeredExtractFor
      // exists for: re-register from memory rather than query the wrong window.
      if (sampledGeneration !== undefined) {
        const live = registeredExtractFor(engine, req);
        if (live && live.generation > sampledGeneration) extract = live;
      }
      await registerIfNeeded(engine, req, extract);
      // A network answer (a 304 included) restarts the recheck window.
      const registered = engine.registered.get(req.key);
      if (fromNetwork && registered) {
        registered.checkedAt = Date.now();
      }
      builtAt.set(req.key, extract.builtAt);
    }
    return run({
      builtAt: key => builtAt.get(key) ?? null,
      // The registered view's ACTUAL schema, names AND types — a
      // Parquet-metadata-only probe (LIMIT 0 reads no rows; the Arrow result's
      // schema.fields carry the column types). The runner's extract-schema gate
      // uses this to re-validate joined definitions against what the fetched
      // extract really contains, because the manifest describes what the server
      // would serve NOW, not what this session already fetched.
      columns: async datasetKey => {
        const result = await engine.conn.query(
          `SELECT * FROM ${q(datasetKey)} LIMIT 0`
        );
        return new Map(
          result.schema.fields.map(f => [
            f.name,
            arrowFieldTypeToColumnType(f.type),
          ])
        );
      },
      query: async sql => {
        const start = performance.now();
        const result = await engine.conn.query(sql);
        logTiming('query_run', start);
        return {
          columns: result.schema.fields.map(f => f.name),
          rows: arrowToRows(result),
        };
      },
    });
  });
}

/**
 * The scope currently registered for a dataset, or FULL_SCOPE when it has never
 * been registered.
 *
 * For consumers that piggyback on whatever the report runner already loaded (the
 * filter-value combobox): asking for a scope of their own would either fight the
 * runner for the single view or, worse, pull the WHOLE dataset down just to
 * populate a dropdown. Reading the current scope keeps them on the bytes that
 * are already local and makes the values they offer exactly the values the
 * report can actually match.
 */
export function currentScopeFor(datasetKey: string): ExtractScope {
  return registeredScopes.get(datasetKey) ?? FULL_SCOPE;
}

// Bench-only: a private engine instance the caller MUST dispose. Production code
// keeps using the shared singleton via getEngine(); this exists so the benchmark
// can boot, measure, and fully tear down engines without touching app state (it
// never mutates the shared enginePromise, so a bench run cannot drop the app's
// registered datasets).
export interface IsolatedEngine {
  db: AsyncDuckDB;
  conn: AsyncDuckDBConnection;
  worker: Worker;
}

export async function createIsolatedEngine(): Promise<IsolatedEngine> {
  return initEngine();
}

export async function disposeIsolatedEngine(
  engine: IsolatedEngine
): Promise<void> {
  await engine.conn.close();
  await engine.db.terminate();
  engine.worker.terminate();
}

export function resetEngineForTests(): void {
  enginePromise = null;
  inflightFetches.clear();
  registeredScopes.clear();
  registeredFileNames.clear();
  fileRegistrationSeq = 0;
  sessionChain = Promise.resolve();
}
